import React, { useEffect, useState, useCallback } from "react";
import { useOrderReportQuery } from "../../../Store/Apis/reportApi";
import { useOutletsQuery } from "../../../Store/Apis/outletApi";
import Loader from "../../../Components/Loader/Loader";
import Error from "../../../Components/Error/Error";
import {
  usePaymentTypesQuery,
  useStatusQuery,
} from "../../../Store/Apis/bookingApi";
import downloadPDF from "../../../Hooks/downloadReportPdf";
import { Link } from "react-router-dom";
import { isDropdownPermission } from "../../../Hooks/permissionsMiddleware";
import { useSelector } from "react-redux";
import { FaRegFilePdf, FaFileExcel, FaSearch } from "react-icons/fa";
import formatDate from "../../../Hooks/formatDate";
import * as XLSX from "xlsx";

// Helper function to get formatted date (YYYY-MM-DD)
const getFormattedDate = (date) => {
  return date.toISOString().split("T")[0];
};

const Orders = () => {
  const user = useSelector((state) => state.auth.user);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const [startDate, setStartDate] = useState(getFormattedDate(yesterday));
  const [endDate, setEndDate] = useState(getFormattedDate(today));
  const [keyID, setKeyID] = useState("");
  const [keynum, setKeynum] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [filter, setFilter] = useState({
    paymentType: "",
    customerName: "",
    orderStatus: "",
  });

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Handle search submit on Enter key
  const handleSearchSubmit = useCallback(
    (e) => {
      if (e.key === "Enter") {
        setDebouncedSearchTerm(searchTerm);
      }
    },
    [searchTerm],
  );

  // Handle search button click
  const handleSearchClick = useCallback(() => {
    setDebouncedSearchTerm(searchTerm);
  }, [searchTerm]);

  // Clear search
  const handleClearSearch = useCallback(() => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
  }, []);

  const outletList = useOutletsQuery();
  const statusList = useStatusQuery();
  const paymentTypes = usePaymentTypesQuery();
  const {
    data: Report,
    isSuccess,
    isLoading,
    isFetching,
    isError,
  } = useOrderReportQuery(
    {
      startDate,
      endDate,
      keyID,
      keynum,
      paymentType: filter.paymentType,
      orderStatus: filter.orderStatus,
      // Don't send customerName to API, handle client-side
    },
    { skip: !startDate },
  );

  // PAGINATION CODE
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

  // Client-side filtering for customer search
  const filteredOrders = React.useMemo(() => {
    if (!Report?.data?.orders) return [];

    return Report.data.orders.filter((order) => {
      // Customer name search - case insensitive, trim whitespace, handle null/undefined
      let customerMatch = true;
      if (debouncedSearchTerm) {
        const searchTerm = debouncedSearchTerm.toLowerCase().trim();
        const customerName = (order.customerName || "").toLowerCase().trim();
        customerMatch = customerName.includes(searchTerm);

        // Debug logging (remove in production)
        if (order.customerName?.toLowerCase().includes("riaan")) {
          console.log(
            "Customer found:",
            order.customerName,
            "Search term:",
            debouncedSearchTerm,
            "Match:",
            customerMatch,
          );
        }
      }

      // Payment type filter
      const paymentMatch =
        !filter.paymentType ||
        order.paymentTypeId === parseInt(filter.paymentType);

      // Status filter
      const statusMatch =
        !filter.orderStatus || order.statusId === parseInt(filter.orderStatus);

      return customerMatch && paymentMatch && statusMatch;
    });
  }, [
    Report?.data?.orders,
    debouncedSearchTerm,
    filter.paymentType,
    filter.orderStatus,
  ]);

  const totalPages =
    filteredOrders.length > 0
      ? Math.ceil(filteredOrders.length / itemsPerPage)
      : 0;
  const paginatedData = filteredOrders.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, filter.paymentType, filter.orderStatus]);

  const renderPagination = () => {
    const pages = [];
    const pageLimit = 5;
    const startPage = Math.max(2, currentPage - 2);
    const endPage = Math.min(totalPages - 1, currentPage + 2);

    if (totalPages > 1) {
      pages.push(
        <li
          key={1}
          className={`page-item ${1 === currentPage ? "active" : ""}`}
        >
          <button className="page-link" onClick={() => handlePageChange(1)}>
            1
          </button>
        </li>,
      );

      if (startPage > 2) {
        pages.push(
          <li key="start-ellipsis" className="page-item disabled">
            <span className="page-link">...</span>
          </li>,
        );
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(
          <li
            key={i}
            className={`page-item ${i === currentPage ? "active" : ""}`}
          >
            <button className="page-link" onClick={() => handlePageChange(i)}>
              {i}
            </button>
          </li>,
        );
      }

      if (endPage < totalPages - 1) {
        pages.push(
          <li key="end-ellipsis" className="page-item disabled">
            <span className="page-link">...</span>
          </li>,
        );
      }

      pages.push(
        <li
          key={totalPages}
          className={`page-item ${totalPages === currentPage ? "active" : ""}`}
        >
          <button
            className="page-link"
            onClick={() => handlePageChange(totalPages)}
          >
            {totalPages}
          </button>
        </li>,
      );
    }

    return pages;
  };

  // Calculate totals for filtered data
  const filteredTotalOrders = filteredOrders.length;
  const filteredTotalAmount = filteredOrders.reduce(
    (sum, order) => sum + (order.orderAmount || 0),
    0,
  );

  // GENERATE PDF CODE
  const outlet =
    keyID &&
    outletList?.data?.data.find((elem) => elem.outletId === Number(keyID));
  const outletName = outlet?.outletName || "";
  const status =
    keynum && statusList?.data?.data.find((elem) => elem.id === Number(keynum));
  const statusName = status?.description || "";
  const rows =
    Array.isArray(filteredOrders) &&
    filteredOrders.map((item, index) => [
      index + 1,
      formatDate(item.orderDate),
      item.orderNumber,
      item.customerName,
      item.outletName,
      item.orderAmount,
      item.statusName,
    ]);
  const columns = [
    "Sl No.",
    "Date",
    "Order ID",
    "Customer",
    "Outlet",
    "Order Amount",
    "Status",
  ];

  useEffect(() => {
    if (!isDropdownPermission(user.role_Id)) {
      setKeyID(user.outletID);
    }
  }, []);

  // Excel Download Function
  const downloadExcel = () => {
    if (
      !filteredOrders ||
      !Array.isArray(filteredOrders) ||
      filteredOrders.length === 0
    ) {
      alert("No data available for download");
      return;
    }

    // Prepare data for Excel
    const excelData = filteredOrders.map((order, index) => ({
      "Sl No.": index + 1,
      Date: formatDate(order.orderDate),
      "Order ID": order.orderNumber || "",
      Customer: order.customerName || "N/A",
      // Outlet: order.outletName || "",
      "Order Amount": order.orderAmount,
      Status: order.statusName || "",
    }));

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Set column widths for better formatting
    const columnWidths = [
      { wch: 8 }, // Sl No.
      { wch: 12 }, // Date
      { wch: 15 }, // Order ID
      { wch: 20 }, // Customer
      { wch: 20 }, // Outlet
      { wch: 15 }, // Order Amount
      { wch: 15 }, // Status
    ];
    worksheet["!cols"] = columnWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, "Order Report");

    // Generate filename with date range
    const filename = `Order_Report_${formatDate(startDate)}-${formatDate(
      endDate,
    )}.xlsx`;

    // Download the file
    XLSX.writeFile(workbook, filename);
  };

  return (
    <div>
      <div className="row align-items-center justify-content-between mb-2">
        <div className="col">
          <h5 className="fw-500 text-white">Order Report</h5>
        </div>
      </div>
      <div className="card mb-4">
        <div className="card-header p-4">
          <div className="row gy-2">
            <div className="col-md-4">
              <label>Start Date</label>
              <input
                type="date"
                className="form-control"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <label>End Date</label>
              <input
                type="date"
                className="form-control"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <label>Customer/Company</label>
              <div className="position-relative">
                <input
                  name="customerName"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handleSearchSubmit}
                  className="form-control pe-5"
                  placeholder="Enter Customer Name"
                />
                <FaSearch
                  className="position-absolute top-50 translate-middle-y text-muted"
                  style={{ right: "15px", fontSize: "14px" }}
                />
              </div>
              {debouncedSearchTerm && (
                <small className="text-primary mt-1 d-block">
                  Searching for: "{debouncedSearchTerm}" (
                  {filteredOrders.length} matches)
                </small>
              )}
            </div>
            <div className="col-md-4">
              <label>Payment Types</label>
              <select
                className="form-select"
                value={filter?.payment}
                onChange={(e) =>
                  setFilter({
                    ...filter,
                    paymentType: e.target.value,
                  })
                }
                disabled={!paymentTypes.isSuccess}
              >
                <option value="">Select Payment</option>
                {paymentTypes?.data?.data.map((elem) => (
                  <option key={elem.id} value={elem.id}>
                    {elem.paymentType}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <label>Status</label>
              <select
                className="form-select"
                value={filter?.orderStatus}
                onChange={(e) =>
                  setFilter({
                    ...filter,
                    orderStatus: e.target.value,
                  })
                }
                disabled={!statusList.isSuccess}
              >
                <option value="">Select Status</option>
                {statusList?.data?.data.map((elem) => (
                  <option key={elem.id} value={elem.id}>
                    {elem.description}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <label>Outlet name</label>
              <select
                className="form-select"
                value={
                  isDropdownPermission(user.role_Id) ? keyID : user.outletID
                }
                onChange={(e) => setKeyID(e.target.value)}
                disabled={
                  !outletList.isSuccess || !isDropdownPermission(user.role_Id)
                }
              >
                <option value="">All Outlets</option>
                {outletList?.data?.data.map((elem) => (
                  <option key={elem.outletId} value={elem.outletId}>
                    {elem.outletName}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="card-body p-0">
          {isError && <Error />}
          {(isLoading || isFetching) && <Loader height="300px" />}
          {!isLoading && !isFetching && (
            <>
              <div className="table-responsive mb-4">
                <table className="table table-bordered align-items-center mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th
                        style={{ width: "15%" }}
                        className="text-uppercase text-secondary text-xs opacity-7 px-3"
                      >
                        Date
                      </th>
                      <th
                        style={{ width: "10%" }}
                        className="text-uppercase text-secondary text-xs opacity-7 px-3"
                      >
                        Order ID
                      </th>
                      <th
                        style={{ width: "15%" }}
                        className="text-uppercase text-secondary text-xs opacity-7 px-3"
                      >
                        Customer / Company
                      </th>
                      {/* <th
                        style={{ width: "15%" }}
                        className="text-uppercase text-secondary text-xs opacity-7 px-3"
                      >
                        Outlet
                      </th> */}
                      {/* <th style={{ width: '10%' }} className="text-uppercase text-secondary text-xs opacity-7 px-3">
                        Quantity
                      </th> */}
                      <th
                        style={{ width: "10%" }}
                        className="text-uppercase text-secondary text-xs opacity-7 px-3"
                      >
                        Order Amount
                      </th>
                      <th
                        style={{ width: "15%" }}
                        className="text-uppercase text-secondary text-xs opacity-7 px-3"
                      >
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedData.length > 0 ? (
                      paginatedData.map((order) => (
                        <tr key={order.orderNumber}>
                          <td className="text-sm px-3">
                            {formatDate(order.orderDate)}
                          </td>
                          <td className="text-sm px-3">{order.orderNumber}</td>
                          <td className="text-sm px-3">
                            {order.customerName || "N/A"}
                          </td>
                          {/* <td className="text-sm px-3">{order.outletName}</td> */}
                          {/* <td className='text-sm px-3'>{order.quantity}</td> */}
                          <td className="text-sm px-3">
                            R {order.orderAmount.toFixed(2)}
                          </td>
                          <td className="text-sm px-3">{order.statusName}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7} className="text-center py-5">
                          {debouncedSearchTerm ? (
                            <>
                              No orders found for customer "
                              {debouncedSearchTerm}"
                              <br />
                              <small className="text-muted">
                                Try a different search term or{" "}
                                <button
                                  className="btn btn-link p-0 text-primary"
                                  onClick={handleClearSearch}
                                >
                                  clear search
                                </button>
                              </small>
                            </>
                          ) : (
                            "No Data Found !!"
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <nav>
                <ul className="pagination justify-content-center mb-4">
                  {renderPagination()}
                </ul>
              </nav>
              <div className="row align-items-center px-4 mb-3">
                <div className="col">
                  <span className="text-sm mb-0 fw-500">Total Orders:</span>
                  <span className="text-sm text-dark ms-2 fw-600 mb-0">
                    {filteredTotalOrders}
                    {debouncedSearchTerm && (
                      <small className="text-muted ms-1">
                        (filtered from {Report?.data?.totalOrders})
                      </small>
                    )}
                  </span>
                </div>
                <div className="col">
                  <span className="text-sm mb-0 fw-500">
                    Total Order Amount:
                  </span>
                  <span className="text-sm text-dark ms-2 fw-600 mb-0">
                    R {filteredTotalAmount.toFixed(2)}
                    {debouncedSearchTerm && (
                      <small className="text-muted ms-1">
                        (filtered from R {Report?.data?.totalOrderAmount})
                      </small>
                    )}
                  </span>
                </div>
                <div className="col-auto">
                  <button
                    className="btn btn-success me-2"
                    onClick={() =>
                      downloadPDF({
                        title: "Order Report",
                        docName: `Order_Report ${formatDate(
                          startDate,
                        )}-${formatDate(endDate)}${debouncedSearchTerm ? `_${debouncedSearchTerm}` : ""}`,
                        startDate: formatDate(startDate),
                        endDate: formatDate(endDate),
                        outletName,
                        status: statusName || "All",
                        rows,
                        columns,
                        totalOrder: filteredTotalOrders,
                        totalOrderAmout: filteredTotalAmount,
                      })
                    }
                    disabled={
                      !isSuccess ||
                      isLoading ||
                      isFetching ||
                      !Array.isArray(filteredOrders) ||
                      filteredOrders.length === 0
                    }
                  >
                    Download PDF <FaRegFilePdf size={20} />
                  </button>
                  <button
                    className="btn btn-warning me-2"
                    onClick={downloadExcel}
                    disabled={
                      !isSuccess ||
                      isLoading ||
                      isFetching ||
                      !Array.isArray(filteredOrders) ||
                      filteredOrders.length === 0
                    }
                  >
                    Download Excel <FaFileExcel size={20} />
                  </button>
                  {/* <Link
                    to={`print?startDate=${startDate}&endDate=${endDate}&outlet=${keyID}&status=${keynum}`}
                    target="_blank"
                    disabled={!isSuccess || isLoading || isFetching || !Array.isArray(Report?.data.orders)}
                  >
                    <button
                      rel="noopener noreferrer"
                      className="btn btn-warning"
                      disabled={!isSuccess || isLoading || isFetching || !Array.isArray(Report?.data.orders)}
                    >Print Report</button>
                  </Link> */}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Orders;
